<template>
  <div ref="el" class="full-width">
    <div
      class="d-flex align-items-center appointment-event"
      :class="{ 'ghost-appointment': details.ghost }"
      :style="{ backgroundColor: event.backgroundColor }"
      @click="handleEventClick"
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
    >
      <div class="d-flex align-items-center justify-content-between full-width">
        <TruncateText :expand="false" :title="event.title">
          <span v-if="details.has_ghost_appointment" class="held-badge">
            HELD
          </span>
          <span v-else-if="details.ghost" class="held-badge">HOLD</span>
          <i
            v-if="details.assignment_pinned"
            class="fa-regular fa-lock-alt appointment-icon"
          ></i>
          <i
            v-if="details.self_booked"
            class="fa-regular fa-user-large appointment-icon"
          ></i>
          <span>{{ event.title }}</span>
        </TruncateText>
        <div class="d-flex align-items-center flex-shrink-0 pr-2">
          <Tooltip
            v-if="!breakpoints().sm"
            placement="top-start"
            class-name="schedule-board-tooltip appointment-event-tooltip"
            :options="tippyOptions"
          >
            <template #trigger>
              <button
                data-testid="appointment-info-icon"
                type="button"
                class="appointment-info-icon"
                aria-label="Appointment quick details"
                @click.stop.prevent
                @mousedown.stop
              >
                <InfoCircle class="appointment-info-icon__svg" />
              </button>
            </template>

            <template #tooltip>
              <div>
                <div class="d-flex flex-column">
                  <div class="tooltip-section">
                    <div class="tooltip-row">
                      <span>Job #</span>
                      <span>{{ details.job_id }}</span>
                    </div>
                    <div class="tooltip-row">
                      <span>Appointment #</span>
                      <span>{{ appointmentId }}</span>
                    </div>
                    <div class="tooltip-row">
                      <span>Created By</span>
                      <span>
                        <i
                          v-if="details.self_booked"
                          class="fas fa-user fa-xs mr-1 light-icon"
                        ></i>
                        <template v-if="created_by">
                          {{ userTypeLabel }}:
                          {{ created_by.full_name }}
                        </template>
                        <template v-else-if="details.self_booked">
                          Customer
                        </template>
                        <template v-else>Staff</template>
                      </span>
                    </div>
                    <div class="tooltip-row">
                      <span>Revenue</span>
                      <span>{{
                        dollars(details.job_invoice_revenue_cents)
                      }}</span>
                    </div>
                    <div v-if="assignedTechNames" class="tooltip-row">
                      <span>Assigned To</span>
                      <span>
                        <i
                          v-if="details.assignment_pinned"
                          class="fas fa-lock-alt fa-xs mr-1 light-icon"
                        ></i>
                        {{ assignedTechNames }}
                      </span>
                    </div>
                    <div class="tooltip-row">
                      <span>Status</span>
                      <span>{{ statusLabel }}</span>
                    </div>
                    <div class="tooltip-row">
                      <span>Service</span>
                      <span class="sc-name">{{
                        getProperty(details.service_category, "name")
                      }}</span>
                    </div>
                    <div class="tooltip-row">
                      <span>Priority</span>
                      <span v-if="priority === 1"
                        ><i class="fa fa-long-arrow-up mr-1"></i>High</span
                      >
                      <span v-else-if="priority === 2">Normal</span>
                      <span v-else
                        ><i class="fa fa-long-arrow-down mr-1"></i>Low</span
                      >
                    </div>
                    <div v-if="details.job_tags.length" class="tooltip-row">
                      <span>{{
                        details.job_tags.length > 1 ? "Job Tags" : "Job Tag"
                      }}</span>
                      <span class="small-pill-container">
                        <TagPill
                          v-for="tag in details.job_tags"
                          :key="tag.id"
                          :tag="tag"
                          white-label
                          small-pill
                        />
                      </span>
                    </div>
                  </div>

                  <div class="tooltip-section">
                    <div class="tooltip-row">
                      <span>Customer</span>
                      <span>{{ customerName }}</span>
                    </div>
                    <div v-if="customer.parent" class="tooltip-row">
                      <span>Parent</span>
                      <span>{{ parentName }}</span>
                    </div>
                    <div v-if="customer.memberships.length" class="tooltip-row">
                      <span>{{
                        customer.memberships.length > 1
                          ? "Memberships"
                          : "Membership"
                      }}</span>
                      <span>{{ membershipsLabel }}</span>
                    </div>
                    <div class="tooltip-row">
                      <span>Phone</span>
                      <span class="d-flex align-items-center">
                        <i :class="`fas fa-${phoneIcon} mr-2 light-icon`"></i>
                        <div v-if="primaryPhone">
                          {{ primaryPhone.phone_number }}
                        </div>
                        <div v-if="alternatePhones.length" class="ml-1">
                          ({{ alternatePhones.length }})
                        </div>
                      </span>
                    </div>
                    <div class="tooltip-row">
                      <span>Location Name</span>
                      <span>{{ details.address.name }}</span>
                    </div>
                    <div class="tooltip-row">
                      <span>Location</span>
                      <span>
                        <i class="fas fa-home mr-1 light-icon"></i>
                        {{ fullAddress(details.address) }}
                      </span>
                    </div>
                    <div v-if="customer.tags.length" class="tooltip-row">
                      <span>{{
                        customer.tags.length > 1 ? "Tags" : "Tag"
                      }}</span>
                      <span class="small-pill-container">
                        <TagPill
                          v-for="tag in customer.tags"
                          :key="tag.id"
                          :tag="tag"
                          white-label
                          small-pill
                        />
                      </span>
                    </div>
                  </div>

                  <div class="tooltip-section">
                    <div class="tooltip-row">
                      <span>Appointment</span>
                      <span>{{ scheduleWindow }}</span>
                    </div>
                    <div
                      v-if="isTrackable && hasActualTimes"
                      class="tooltip-row"
                    >
                      <span>Actual Time</span>
                      <span>{{
                        details.status === "running"
                          ? runningWindow
                          : actualWindow
                      }}</span>
                    </div>
                    <div
                      v-if="
                        isTrackable &&
                        hasActualTimes &&
                        details.status !== 'running'
                      "
                      class="tooltip-row"
                    >
                      <span>Duration</span>
                      <span
                        ><minutes-label :minutes="jobDuration"></minutes-label
                      ></span>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </Tooltip>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { mapGetters } from "vuex";
import { getMinuteDiff, getProperty } from "@/lib/helpers.js";

import MinutesLabel from "@/components/MinutesLabel.vue";
import Tooltip from "@/components/Tooltip.vue";
import TruncateText from "@/components/TruncateText.vue";
import CONSTANTS from "@/Constants.js";
import InfoCircle from "@/images/info.svg?component";
import { capitalize, dollars } from "@/mixins";
import TagPill from "@/Tags/TagPill.vue";
const { APPOINTMENT_STATUS_MAP } = CONSTANTS;

const BILLS_TO_LABEL = "(Bills To)";

export default {
  components: {
    InfoCircle,
    Tooltip,
    MinutesLabel,
    TruncateText,
    TagPill
  },
  props: {
    event: {
      type: Object,
      default: null
    }
  },
  emits: ["open-edit-modal"],
  data() {
    return {
      untrackableStatuses: ["pending", "installation_pending"],
      actualTimeStatuses: ["completed", "running"]
    };
  },
  computed: {
    ...mapGetters(["featureEnabledForTenant"]),
    userTypeLabel() {
      return capitalize(this.created_by.user_type);
    },
    appointmentId() {
      return this.details._appointmentId || this.event.id;
    },
    details() {
      return this.event.extendedProps;
    },
    assignedTechNames() {
      if (this.details.technicians?.length > 0) {
        return this.details.technicians.map((t) => t.full_name).join(", ");
      }
      return this.details.resource_name;
    },
    created_by() {
      return getProperty(this.details, "created_by");
    },
    customer() {
      return getProperty(this.details, "customer");
    },
    isTrackable() {
      return !this.untrackableStatuses.includes(this.details.status);
    },
    hasActualTimes() {
      return this.actualTimeStatuses.includes(this.details.status);
    },
    statusLabel() {
      return APPOINTMENT_STATUS_MAP[this.details.status];
    },
    primaryPhone() {
      return this.customer.phones.find((phone) => phone.primary);
    },
    alternatePhones() {
      return this.customer.phones.filter((phone) => !phone.primary);
    },
    phoneIcon() {
      return getProperty(this.primaryPhone, "type") === "mobile"
        ? "mobile"
        : "phone";
    },
    scheduleWindow() {
      return this.scheduledDateTime(
        this.details.scheduled_time,
        this.details.arrival_window_start,
        this.details.arrival_window_end,
        false
      );
    },
    actualWindow() {
      return `${moment(this.event.start).format("h:mm a")} to ${moment(
        this.event.end
      ).format("h:mm a")}`;
    },
    runningWindow() {
      return `${moment(this.event.start).format("h:mm a")} - In Progress`;
    },
    jobDuration() {
      return getMinuteDiff(this.event.end, this.event.start);
    },
    priority() {
      return getProperty(this.details.service_category, "priority");
    },
    tippyOptions() {
      return {
        interactive: false,
        delay: [0, 0],
        onShow(instance) {
          tippy.hideAll();
          // Check if appointment is being dragged
          // if it is, disable tooltip
          const parent = $(instance.reference).parent().parent().parent()[0];
          if (
            parent &&
            parent.classList &&
            parent.classList.value.includes("fc-event-dragging")
          ) {
            return false;
          }
        }
      };
    },
    membershipsLabel() {
      return this.customer.memberships
        ?.map((mp) => mp.membership_program_name)
        .join(", ");
    },
    customerName({ customer }) {
      const customerFullName = this.fullName(customer);

      if (customer.parent) {
        if (customer.parent.delegate_billing) {
          return customerFullName;
        } else {
          // has parent but billing not delegated, show that customer has own billing
          return `${customerFullName} ${BILLS_TO_LABEL}`;
        }
      } else {
        return customerFullName;
      }
    },
    parentName({ customer }) {
      if (!customer.parent) {
        return null;
      }

      if (customer.parent.delegate_billing) {
        return `${customer.parent.name} ${BILLS_TO_LABEL}`;
      } else {
        return customer.parent.name;
      }
    }
  },
  methods: {
    getProperty,
    handleMouseEnter() {
      const selector = `.event-id-${this.appointmentId}`;
      const isGhost = this.details.ghost;
      if (isGhost || (!isGhost && $(`${selector}.ghost`).length > 0)) {
        //  Hovered a pending event and there is a ghost for that event or hovered a ghost
        $(selector).addClass("highlighted-event");
      }
    },
    handleMouseLeave() {
      $(".fc-timeline-event").removeClass("highlighted-event");
    },
    handleGhostHover() {
      $(document).on("mouseenter", ".appointment-event", (e) => {
        const classNames = e.currentTarget.classList;
        let eventClass = "";
        let isGhost = false;
        classNames.forEach((value) => {
          if (value.indexOf("event-id-") > -1) {
            eventClass = value;
          }
          if (value.indexOf("ghost") > -1) {
            isGhost = true;
          }
        });

        if (eventClass) {
          const selector = `.fc-timeline-event.${eventClass}`;
          if (isGhost || (!isGhost && $(`${selector}.ghost`).length > 0)) {
            //  Hovered a pending event and there is a ghost for that event or hovered a ghost
            $(selector).addClass("highlighted-event");
          }
        }
      });
    },
    handleEventClick() {
      tippy.hideAll();
      this.$emit("open-edit-modal", this.appointmentId);
    }
  }
};
</script>

<style lang="scss">
.appointment-event {
  position: relative;
  height: 32px;
  border-radius: 4px;
  padding-left: 8px;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.6);
  font-weight: 600;
  letter-spacing: -0.42px;
}
.appointment-event-title {
  position: absolute;
  top: -40px;
}
.appointment-event-tooltip-theme.tippy-tooltip {
  max-width: 350px !important;
}
.fc-event-dragging {
  cursor: grabbing !important;
  overflow: hidden;
}
.held-badge {
  font-weight: 600;
  border-radius: 4px;
  background: var(--neutral-gray-100);
  padding: 1px;
  font-size: 0.8em;
  color: var(--font-title-color);
  height: 15px;
  text-align: center;
  padding-right: 0px;
  margin-right: 5px;
  position: relative;
  top: -1px;
  text-shadow: none !important;
}
.ghost-appointment {
  border: 2px dashed var(--neutral-gray-700) !important;
  background: var(--neutral-gray-400) !important;
  color: #353a3b !important;
  text-shadow: none !important;
}
.appointment-info-icon {
  cursor: pointer;
  text-shadow: none;
  margin-right: 4px;
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  display: flex;
  align-items: center;
}
.appointment-info-icon__svg {
  width: 14px;
  height: 14px;
  opacity: 0.9;
  color: rgba(255, 255, 255, 0.9);
}
.fc .schedule-event.available .fc-event-resizer,
.fc .schedule-event.exclusive .fc-event-resizer {
  display: none !important;
}
.fc .schedule-event.available,
.fc .schedule-event.exclusive {
  cursor: default !important;
}
</style>
